import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './NewDashboard.css';
import './AdminDashboard.css';
import ActiveRegionalBadge from './common/ActiveRegionalBadge';
import { useAuth } from '../contexts/AuthContext';
import RegionalService from '../services/RegionalService';
import areaService from '../services/AreaService';
import materiaService from '../services/MateriaService';
import adminAsistenteService from '../services/AdminAsistenteService';
import AsistenteService from '../services/AsistenteService';
import { filterAdminMenuItems, isAdminArea } from '../utils/adminMenu';
import tipoAsistenteService from '../services/TipoAsistenteService';
const INSTITUCIONAL_ID = '61A351C4-64C5-42A3-88D1-E27F092551CD';
const PROMPT_FEATURES = [
  'Usa exclusivamente los PDFs adjuntos (RAG obligatorio).',
  'Ejecuta file_search antes de responder consultas académicas.',
  'Si no hay evidencia en los PDFs, responde con el mensaje de falta de información.',
  'Respuestas académicas en HTML con citas de PDF y número de página.',
  'Puede responder saludos o uso del asistente sin citar PDFs.',
  'Imágenes o esquemas solo si existen en PDFs o son representaciones fieles con aviso.',
  'Puede armar planes de estudio y actuar como tutor orientado a ayudar al alumno.'
];

const menuItems = [
  { id: 'resumen', label: 'Dashboard', icon: 'fas fa-chart-line' },
  { id: 'profesores', label: 'Profesores', icon: 'fas fa-chalkboard-teacher' },
  // { id: 'alumnos', label: 'Alumnos', icon: 'fas fa-user-graduate' },
  { id: 'areas', label: 'Areas', icon: 'fas fa-sitemap' },
  { id: 'admin-area', label: 'Admins de área', icon: 'fas fa-user-tie' },
  { id: 'materias', label: 'Materias', icon: 'fas fa-book' },
  { id: 'asistentes', label: 'Asistentes OpenAI', icon: 'fas fa-robot' },
  { id: 'asistentes-gpt', label: 'Asistentes GPT', icon: 'fas fa-robot' },
];

const AdminCreateAsistente = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const activeRegional = useMemo(() => RegionalService.getActiveRegional(), []);
  const activeRegionalId = useMemo(() => RegionalService.getActiveRegionalId(), []);
  const activeRegionalName = activeRegional?.nombre || activeRegional?.name || activeRegional?.descripcion || 'Regional seleccionado';
  const isGptClassicEnabled = useMemo(() => {
    const flags = RegionalService.getFeatureFlags();
    return Array.isArray(flags) && flags.some((flag) => {
      const nombre = (flag?.nombre || flag?.Nombre || '').toLowerCase();
      return nombre === 'asistentesclasicos gpt' && Boolean(flag?.activo ?? flag?.Activo);
    });
  }, []);
  const filteredMenuItems = useMemo(() => filterAdminMenuItems(menuItems, user).filter((item) => isGptClassicEnabled || isAdminArea(user) || item.id !== 'asistentes-gpt'), [user, isGptClassicEnabled]);
  const canSelectTipo = true;

  const [form, setForm] = useState({
    nombre: '',
    nicknameUrl: '',
    idTipoAsistente: INSTITUCIONAL_ID,
    descripcion: '',
    promptExtra: '',
    idRegional: activeRegionalId || '',
    idArea: '',
    idMateria: '',
    publico: true,
    passwordAsistente: ''
  });
  const [areas, setAreas] = useState([]);
  const [materias, setMaterias] = useState([]);
  const [tiposAsistente, setTiposAsistente] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('');
  const [archivosPdf, setArchivosPdf] = useState([]);
  const [isDragOver, setIsDragOver] = useState(false);

  useEffect(() => {
    const fetchAreasMaterias = async () => {
      try {
        const tasks = [
          areaService.getAreasActivas(activeRegionalId),
          materiaService.getMateriasActivas()
        ];
        if (canSelectTipo) {
          tasks.push(tipoAsistenteService.getTiposActivos());
        }
        const [areasList, materiasList, tiposList] = await Promise.all(tasks);
        const filteredMaterias = (materiasList || []).filter((m) => (m.idRegional || m.IdRegional) === activeRegionalId);
        setAreas(Array.isArray(areasList) ? areasList : []);
        setMaterias(Array.isArray(filteredMaterias) ? filteredMaterias : []);
        if (canSelectTipo) {
          setTiposAsistente(Array.isArray(tiposList) ? tiposList : []);
        }
      } catch (error) {
        setMessage(error.message || 'No pudimos cargar áreas o materias.');
        setMessageType('error');
      }
    };
    if (activeRegionalId) {
      fetchAreasMaterias();
    }
  }, [activeRegionalId, canSelectTipo]);

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleNicknameChange = (value) => {
    const normalized = value
      .toLowerCase()
      .replace(/\s+/g, '-')
      .replace(/[^a-z0-9-]/g, '')
      .replace(/-+/g, '-');
    handleChange('nicknameUrl', normalized);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.nombre.trim()) {
      setMessage('Completa el nombre del asistente.');
      setMessageType('error');
      return;
    }
    if (!form.idArea) {
      setMessage('Selecciona un área.');
      setMessageType('error');
      return;
    }
    if (archivosPdf.length === 0) {
      setMessage('Sube al menos un PDF paral asistente.');
      setMessageType('error');
      return;
    }
    if (!form.publico && !form.passwordAsistente.trim()) {
      setMessage('Completa el password del asistente.');
      setMessageType('error');
      return;
    }

    setLoading(true);
    setMessage('Creando asistente...');
    setMessageType('info');
    try {
      // Paso 1: crear recurso en backend admin
      const tipoId = canSelectTipo ? (form.idTipoAsistente || INSTITUCIONAL_ID) : INSTITUCIONAL_ID;
      await adminAsistenteService.createAsistente({
        nombre: form.nombre,
        idTipoAsistente: tipoId,
        descripcion: form.descripcion,
        promptExtra: form.promptExtra,
        nicknameUrl: form.nicknameUrl,
        idRegional: activeRegionalId,
        idArea: form.idArea,
        idMateria: form.idMateria || null,
        publico: form.publico,
        conPassword: !form.publico,
        passwordAsistente: !form.publico ? form.passwordAsistente.trim() : null
      });
      // Paso 2: enviar PDFs al servicio existente (mismo flujo UI que profesor)
      const fd = new FormData();
      fd.append('nombre', form.nombre);
      fd.append('descripcion', form.descripcion || '');
      if (form.promptExtra?.trim()) {
        fd.append('promptExtra', form.promptExtra.trim());
      }
      if (form.nicknameUrl?.trim()) {
        fd.append('nicknameUrl', form.nicknameUrl.trim());
      }
      fd.append('idRegional', activeRegionalId || '');
      fd.append('idArea', form.idArea);
      if (form.idMateria) {
        fd.append('idMateria', form.idMateria);
      }
      if (tipoId) {
        fd.append('idTipoAsistente', tipoId);
      }
      fd.append('publico', String(form.publico));
      fd.append('conPassword', String(!form.publico));
      if (!form.publico && form.passwordAsistente.trim()) {
        fd.append('passwordAsistente', form.passwordAsistente.trim());
      }
      archivosPdf.forEach((file) => fd.append('archivosPdf', file));
      const createResponse = await AsistenteService.crearAsistente(fd);
      const assistantId = createResponse?.idOpenAIAssistant || createResponse?.IdOpenAIAssistant;
      if (assistantId && (form.publico !== true || form.passwordAsistente.trim())) {
        await adminAsistenteService.updateAsistente(assistantId, {
          nombre: form.nombre,
          idTipoAsistente: tipoId,
          descripcion: form.descripcion,
          promptExtra: form.promptExtra,
          nicknameUrl: form.nicknameUrl,
          idRegional: activeRegionalId,
          idArea: form.idArea,
          idMateria: form.idMateria || null,
          publico: form.publico,
          conPassword: !form.publico,
          ...(!form.publico && form.passwordAsistente.trim()
            ? { passwordAsistente: form.passwordAsistente.trim() }
            : {})
        });
      }

      setMessage('Asistente creado correctamente.');
      setMessageType('success');
      setTimeout(() => navigate('/admin-dashboard', { replace: true, state: { tab: 'asistentes' } }), 900);
    } catch (error) {
      setMessage(error.message || 'No pudimos crear el asistente.');
      setMessageType('error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="new-dashboard admin-mode">
      <aside className="dashboard-sidebar">
        <div className="sidebar-header">
          <div className="sidebar-logo">
            <i className="fas fa-university"></i>
            <span>Admin</span>
          </div>
        </div>
        <nav className="sidebar-nav">
          {filteredMenuItems.map((item) => (
            <button
              key={item.id}
              className={`nav-item ${item.id === 'asistentes' ? 'active' : ''}`}
              onClick={() => navigate('/admin-dashboard', { state: { tab: item.id } })}
            >
              <i className={item.icon}></i>
              <span>{item.label}</span>
            </button>
          ))}
        </nav>
        <div className="sidebar-footer">
          <ActiveRegionalBadge />
          <div className="sidebar-user">
            <div className="user-avatar">
              <i className="fas fa-user-shield"></i>
            </div>
            <div className="user-info">
              <span className="user-name">{user?.displayName || user?.name || user?.email || 'Administrador'}</span>
              <span className="user-role">Admin</span>
            </div>
          </div>
        </div>
      </aside>

      <main className="dashboard-main">
        <header className="dashboard-header">
          <div className="header-left">
            <h1 className="page-title">Crear asistente</h1>
            <p className="page-subtitle">Asigna un asistente al regional {activeRegionalName}. Área obligatoria, materia opcional.</p>
          </div>
          <div className="header-right">
            <ActiveRegionalBadge />
            <div className="user-menu">
              <button className="user-menu-trigger" onClick={logout}>
                <div className="user-avatar">
                  <i className="fas fa-user-shield"></i>
                </div>
                <i className="fas fa-sign-out-alt"></i>
              </button>
            </div>
          </div>
        </header>

        <div className="dashboard-content" style={{ padding: '2rem' }}>
          <div className="content-card">
            <div className="card-header">
              <div className="card-title">
                <i className="fas fa-robot" />
                <span>Datos del asistente</span>
              </div>
            </div>
            <div className="card-content">
              <p style={{ marginTop: 0, color: '#475569' }}>
                Completa la información. El área es obligatoria, la materia puede quedar vacía.
              </p>
              <form onSubmit={handleSubmit} className="prof-create-form">
                <div className="assistant-form-grid">
                  <div className="assistant-form-panel">
                <div className="form-grid">
                  <div className="form-field">
                    <label>Nombre</label>
                    <input
                      type="text"
                      value={form.nombre}
                      onChange={(e) => handleChange('nombre', e.target.value)}
                      placeholder="Asistente X"
                    />
                  </div>
                  <div className="form-field">
                    <label>Nickname URL</label>
                    <input
                      type="text"
                      value={form.nicknameUrl}
                      onChange={(e) => handleNicknameChange(e.target.value)}
                      placeholder="ej: algebra-lineal-1"
                    />
                  </div>
                  <div className="form-field">
                    <label>Tipo de asistente</label>
                    {canSelectTipo ? (
                      <select
                        value={form.idTipoAsistente || INSTITUCIONAL_ID}
                        onChange={(e) => handleChange('idTipoAsistente', e.target.value)}
                      >
                        <option value="">Selecciona un tipo</option>
                        {tiposAsistente.map((tipo) => (
                          <option key={tipo.id || tipo.Id} value={tipo.id || tipo.Id}>
                            {tipo.nombre || tipo.Nombre}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <input type="text" value="Institucional" disabled />
                    )}
                  </div>
                  <div className="form-field">
                    <label>Área (obligatorio)</label>
                    <select value={form.idArea} onChange={(e) => handleChange('idArea', e.target.value)}>
                      <option value="">Selecciona un área</option>
                      {areas.map((a) => (
                        <option key={a.id || a.Id} value={a.id || a.Id}>
                          {a.nombre || a.Nombre}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="form-field">
                    <label>Materia (opcional)</label>
                    <select value={form.idMateria} onChange={(e) => handleChange('idMateria', e.target.value)}>
                      <option value="">Sin materia</option>
                      {materias.map((m) => (
                        <option key={m.id || m.Id} value={m.id || m.Id}>
                          {m.nombre || m.Nombre}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="form-field">
                  <label>Descripción</label>
                  <textarea
                    value={form.descripcion}
                    onChange={(e) => handleChange('descripcion', e.target.value)}
                    placeholder="Descripción opcional"
                    style={{ borderRadius: '10px', border: '1px solid var(--gray-200)', padding: '0.65rem 0.85rem', fontSize: '0.95rem', minHeight: '80px' }}
                  />
                </div>

                <div className="form-grid">
                  <div className="form-field">
                    <label>Visibilidad</label>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
                      <input
                        type="checkbox"
                        checked={form.publico}
                        onChange={(e) => {
                          const nextValue = e.target.checked;
                          setForm((prev) => ({
                            ...prev,
                            publico: nextValue,
                            passwordAsistente: nextValue ? '' : prev.passwordAsistente
                          }));
                        }}
                      />
                      <span>Asistente público</span>
                    </div>
                  </div>
                </div>

                {!form.publico && (
                  <div className="form-field">
                    <label>Password</label>
                    <input
                      type="text"
                      value={form.passwordAsistente}
                      onChange={(e) => handleChange('passwordAsistente', e.target.value)}
                      placeholder="Ingresa el password (obligatorio)"
                    />
                  </div>
                )}

                <div className="form-section files-section" style={{ padding: 0, marginTop: '1rem' }}>
                  <div className="section-header" style={{ paddingLeft: 0 }}>
                    <div className="section-icon">
                      <i className="fas fa-file-pdf"></i>
                    </div>
                    <div className="section-text">
                      <h3 className="section-title" style={{ margin: 0 }}>Documentos de conocimiento</h3>
                      <p className="section-description" style={{ margin: 0 }}>Sube los PDFs igual que en la UI de profesor.</p>
                    </div>
                  </div>

                  <div
                    className={`file-dropzone ${isDragOver ? 'dragover' : ''} ${archivosPdf.length > 0 ? 'has-files' : ''}`}
                    onDragOver={(e) => {
                      e.preventDefault();
                      setIsDragOver(true);
                    }}
                    onDragLeave={() => setIsDragOver(false)}
                    onDrop={(e) => {
                      e.preventDefault();
                      setIsDragOver(false);
                      const files = Array.from(e.dataTransfer.files || []).filter((f) => f.type === 'application/pdf');
                      setArchivosPdf((prev) => [...prev, ...files].slice(0, 20));
                    }}
                  >
                    <div className="dropzone-border">
                      <div className="dropzone-content">
                        <div className="dropzone-icon">
                          <i className="fas fa-cloud-upload-alt"></i>
                        </div>
                        <div className="dropzone-text">
                          <h4>Arrastra y suelta tus archivos PDF aquí</h4>
                          <p>Hasta 20 archivos, máximo 10MB c/u</p>
                        </div>
                        <div className="dropzone-divider">
                          <span>o</span>
                        </div>
                        <label className="file-input-label">
                          <input
                            type="file"
                            multiple
                            accept=".pdf"
                            onChange={(e) => {
                              const files = Array.from(e.target.files || []).filter((f) => f.type === 'application/pdf');
                              setArchivosPdf((prev) => [...prev, ...files].slice(0, 20));
                            }}
                            disabled={loading}
                            style={{ display: 'none' }}
                          />
                          <span className="btn btn-upload">
                            <i className="fas fa-folder-open"></i>
                            Seleccionar Archivos PDF
                          </span>
                        </label>
                        <div className="upload-limits">
                          <div className="limit-item">
                            <i className="fas fa-file-alt"></i>
                            <span>Máximo 20 archivos</span>
                          </div>
                          <div className="limit-item">
                            <i className="fas fa-file-pdf"></i>
                            <span>Solo formato PDF</span>
                          </div>
                          <div className="limit-item">
                            <i className="fas fa-weight-hanging"></i>
                            <span>Hasta 10MB por archivo</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {archivosPdf.length > 0 && (
                    <div className="selected-files">
                      <h4>Archivos seleccionados ({archivosPdf.length}/20):</h4>
                      <div className="files-list">
                        {archivosPdf.map((file, index) => (
                          <div key={index} className="file-item">
                            <div className="file-info">
                              <i className="fas fa-file-pdf"></i>
                              <span className="file-name">{file.name}</span>
                              <span className="file-size">
                                {(file.size / 1024 / 1024).toFixed(2)} MB
                              </span>
                            </div>
                            <button
                              type="button"
                              className="remove-file-btn"
                              onClick={() => setArchivosPdf((prev) => prev.filter((_, i) => i !== index))}
                              disabled={loading}
                              title="Eliminar archivo"
                            >
                              <i className="fas fa-times"></i>
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {message && (
                  <div
                    className="modal-message"
                    style={{
                      marginTop: '0.5rem',
                      borderColor: messageType === 'success' ? '#bbf7d0' : messageType === 'error' ? '#fecaca' : 'var(--gray-200)',
                      background: messageType === 'success' ? '#dcfce7' : messageType === 'error' ? '#fee2e2' : '#f8fafc',
                      color: messageType === 'error' ? '#7f1d1d' : messageType === 'success' ? '#0f5132' : 'var(--gray-800)'
                    }}
                  >
                    {loading && <i className="fas fa-spinner fa-spin" style={{ marginRight: '0.35rem' }} />}
                    {message}
                  </div>
                )}

                  </div>

                  <aside className="assistant-prompt-panel">
                    <div className="prompt-panel-header">
                      <i className="fas fa-clipboard-list"></i>
                      <div>
                        <h3>Comportamiento del asistente</h3>
                        <p>Resumen del prompt base configurado.</p>
                      </div>
                    </div>
                    <ul className="prompt-features-list">
                      {PROMPT_FEATURES.map((item) => (
                        <li key={item}>
                          <i className="fas fa-check-circle"></i>
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                    <div className="prompt-extra-field">
                      <label className="form-label">
                        <i className="fas fa-pen"></i>
                        Aclaraciones extra (opcional)
                      </label>
                      <textarea
                        className="form-input enhanced"
                        rows="6"
                        value={form.promptExtra}
                        onChange={(e) => handleChange('promptExtra', e.target.value)}
                        placeholder="Ej: prioriza ejercicios resueltos, responde en español neutro..."
                        disabled={loading}
                      />
                      <div className="input-hint">
                        <i className="fas fa-info-circle"></i>
                        Estas reglas se agregan al prompt base.
                      </div>
                    </div>
                  </aside>
                </div>

                <div className="form-actions">
                  <button type="button" className="btn btn-secondary" onClick={() => navigate('/admin-dashboard', { state: { tab: 'asistentes' } })}>
                    Cancelar
                  </button>
                  <button type="submit" className="btn btn-primary" disabled={loading || archivosPdf.length === 0}>
                    {loading ? <i className="fas fa-spinner fa-spin" /> : <i className="fas fa-save" />}
                    <span>Crear asistente</span>
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default AdminCreateAsistente;
